import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import styles from './andersoncruz_personal_consultoriapersonalizada.module.scss';
import WhatsappIcon from '../../icons/whatsappIcon.tsx';

const WHATSAPP_NUMBER = '5517981132120';

const wa = (text: string) =>
    `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

const MSG_MENSAL =
    'Olá, Anderson! Vi a página da consultoria online personalizada e quero saber mais sobre o plano mensal (a partir de R$ 99,99). Podemos conversar?';
const MSG_TRIMESTRAL =
    'Olá, Anderson! Tenho interesse no plano trimestral da consultoria online personalizada (a partir de R$ 290,00 ou R$ 300,00 em até 3x). Podemos alinhar?';
const MSG_SEMESTRAL =
    'Olá, Anderson! Quero entender o plano semestral da consultoria online personalizada (a partir de R$ 579,00 ou R$ 600,00 em até 6x). Podemos conversar?';
const MSG_GERAL =
    'Olá, Anderson! Li sobre a consultoria online personalizada e quero sua ajuda para escolher o melhor plano para mim. Podemos conversar?';

const Andersoncruz_personal_consultoriapersonalizada: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className={styles.andersoncruz_personal_consultoriapersonalizada}>
            <div className={styles.page}>
                <header className={styles.header}>
                    <Link
                        to="/andersoncruz_personal"
                        state={{ skipLoader: true }}
                        className={styles.backButton}
                        aria-label="Voltar ao cartão digital"
                    >
                        <FaArrowLeft />
                    </Link>
                    <div className={styles.headerText}>
                        <p className={styles.eyebrow}>Anderson Cruz</p>
                        <h1 className={styles.title}>Consultoria On-line Personalizada</h1>
                    </div>
                </header>

                <p className={styles.intro}>
                    Treino individualizado, com acompanhamento profissional à distância, para quem quer qualidade,
                    segurança e estratégia, com a praticidade de treinar no seu ritmo e no seu ambiente.
                </p>

                <div className={styles.contentFlow}>
                    <section className={styles.section} aria-labelledby="sobre-heading">
                        <h2 id="sobre-heading" className={styles.sectionTitle}>
                            Sobre a consultoria
                        </h2>
                        <p className={styles.prose}>
                            A consultoria online é um acompanhamento profissional criado para pessoas que desejam treinar
                            com qualidade, segurança e estratégia, mas que não conseguem ter um acompanhamento presencial
                            no momento ou preferem a praticidade do atendimento online.
                        </p>
                        <p className={styles.prose}>
                            Ela também é ideal para alunos mais avançados, que já treinam sozinhos, porém sentem
                            necessidade de um direcionamento profissional para continuar evoluindo, melhorar a execução,
                            aumentar performance, evitar lesões e ter uma periodização organizada.
                        </p>
                        <p className={styles.prose}>
                            O principal objetivo da consultoria é entregar um treino totalmente individualizado,
                            respeitando a realidade, rotina, limitações e objetivos de cada aluno.
                        </p>
                    </section>

                    <section className={styles.section} aria-labelledby="para-quem-heading">
                        <h2 id="para-quem-heading" className={styles.sectionTitle}>
                            Para quem essa consultoria serve?
                        </h2>
                        <div className={styles.audienceGrid}>
                            <article className={styles.audienceCard}>
                                <h3 className={styles.audienceTitle}>Pessoas com rotina corrida</h3>
                                <p className={styles.audienceText}>
                                    Quem trabalha muito, tem horários limitados ou dificuldade para conciliar agenda com
                                    atendimento presencial.
                                </p>
                            </article>
                            <article className={styles.audienceCard}>
                                <h3 className={styles.audienceTitle}>Treino em academia sem acompanhamento</h3>
                                <p className={styles.audienceText}>
                                    Alunos que treinam sozinhos e querem sair do “treino aleatório”, passando a ter uma
                                    estratégia profissional.
                                </p>
                            </article>
                            <article className={styles.audienceCard}>
                                <h3 className={styles.audienceTitle}>Iniciantes</h3>
                                <p className={styles.audienceText}>
                                    Quem precisa aprender a treinar da forma correta, com segurança e progressão adequada.
                                </p>
                            </article>
                            <article className={styles.audienceCard}>
                                <h3 className={styles.audienceTitle}>Avançados</h3>
                                <p className={styles.audienceText}>
                                    Quem já tem experiência, mas busca evolução contínua, performance, hipertrofia,
                                    emagrecimento ou condicionamento.
                                </p>
                            </article>
                            <article className={styles.audienceCard}>
                                <h3 className={styles.audienceTitle}>Limitações ou desconfortos</h3>
                                <p className={styles.audienceText}>
                                    Dores articulares, restrições de movimento, lesões antigas ou necessidade de
                                    adaptações individuais.
                                </p>
                            </article>
                        </div>
                    </section>

                    <section className={styles.section} aria-labelledby="como-funciona-heading">
                        <h2 id="como-funciona-heading" className={styles.sectionTitle}>
                            Como funciona a consultoria online?
                        </h2>

                        <article className={styles.stepBlock}>
                            <h3 className={styles.stepTitle}>1. Questionário de anamnese</h3>
                            <p className={styles.prose}>
                                O primeiro passo é preencher um questionário completo. Nele avaliamos objetivos, rotina,
                                experiência com treino, histórico de lesões, dores, sono, estresse, frequência,
                                disponibilidade semanal, limitações e preferências de exercícios.
                            </p>
                            <p className={styles.noteTitle}>Por que isso é importante?</p>
                            <p className={styles.prose}>
                                A anamnese permite entender completamente o aluno antes da montagem do treino, garantindo
                                um planejamento seguro, eficiente e totalmente personalizado.
                            </p>
                        </article>

                        <article className={styles.stepBlock}>
                            <h3 className={styles.stepTitle}>2. Avaliação física e postural</h3>
                            <p className={styles.prose}>
                                Analisamos postura, assimetrias, mobilidade, pontos de fraqueza, dificuldades de
                                movimento e acompanhamento da evolução (medidas e fotos).
                            </p>
                            <p className={styles.noteTitle}>Objetivo da avaliação</p>
                            <p className={styles.prose}>
                                Identificar limitações e necessidades específicas para adaptar os exercícios e melhorar
                                desempenho, postura e segurança.
                            </p>
                        </article>

                        <article className={styles.stepBlock}>
                            <h3 className={styles.stepTitle}>3. Treino personalizado no aplicativo</h3>
                            <p className={styles.prose}>
                                Você recebe um programa montado conforme objetivos, nível, rotina, disponibilidade,
                                limitações e condicionamento atual.
                            </p>
                            <ul className={styles.bulletList}>
                                <li>Divisão completa de treino, séries, repetições e descanso</li>
                                <li>Técnicas de execução e controle de cargas</li>
                                <li>Registro de evolução</li>
                                <li>Vídeos reais demonstrando os exercícios</li>
                            </ul>
                            <p className={styles.noteTitle}>Diferencial</p>
                            <p className={styles.prose}>
                                Todos os exercícios com vídeos reais facilitam o aprendizado e a execução correta, com mais
                                segurança e eficiência nos resultados.
                            </p>
                        </article>

                        <article className={styles.stepBlock}>
                            <h3 className={styles.stepTitle}>4. Check-in quinzenal</h3>
                            <p className={styles.prose}>
                                A cada 15 dias, acompanhamento individual: evolução física, adaptação ao treino, cansaço,
                                progressão de cargas, dificuldades, sono, motivação, alimentação e rotina.
                            </p>
                            <p className={styles.noteTitle}>Função do check-in</p>
                            <p className={styles.prose}>
                                Garantir que você continue evoluindo da melhor forma, com ajustes estratégicos sempre que
                                necessário.
                            </p>
                        </article>

                        <article className={styles.stepBlock}>
                            <h3 className={styles.stepTitle}>5. Comunicação direta com o personal</h3>
                            <p className={styles.prose}>
                                Durante toda a consultoria você tem canal direto para dúvidas, orientações e suporte, com
                                correções rápidas, ajustes no treino, mais segurança e aderência ao programa.
                            </p>
                        </article>

                        <article className={styles.stepBlock}>
                            <h3 className={styles.stepTitle}>6. Suporte profissional completo</h3>
                            <p className={styles.prose}>
                                Vai além de “passar treino”: acompanhamento contínuo para melhorar performance, evitar
                                erros, manter consistência, aumentar motivação e evoluir com segurança, mesmo treinando
                                sozinho.
                            </p>
                        </article>
                    </section>

                    <div className={styles.highlightBox}>
                        <h2 className={styles.highlightTitle}>Objetivo da consultoria</h2>
                        <p className={styles.prose}>
                            Entregar um acompanhamento individualizado, estratégico e profissional, ajudando cada aluno
                            a alcançar seus objetivos de forma segura, inteligente e eficiente, independentemente da
                            rotina ou do nível de experiência.
                        </p>
                    </div>

                    <section className={styles.pricingSection} aria-labelledby="planos-heading">
                        <h2 id="planos-heading" className={styles.pricingTitle}>
                            Planos e valores
                        </h2>

                        <div className={styles.planCard}>
                            <p className={styles.planName}>Plano mensal</p>
                            <p className={styles.planPeriod}>A partir de R$ 99,99</p>
                            <p className={styles.planPrice}>Ideal para começar e conhecer a consultoria com baixo risco.</p>
                            <a
                                href={wa(MSG_MENSAL)}
                                className={styles.planWhatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <WhatsappIcon height="18" color="#ffffff" />
                                <span>Quero o plano mensal</span>
                            </a>
                        </div>

                        <div className={styles.planCard}>
                            <p className={styles.planName}>Plano trimestral</p>
                            <p className={styles.planPeriod}>A partir de R$ 290,00 ou R$ 300,00 em até 3x</p>
                            <p className={styles.planPrice}>Para quem busca resultado a curto prazo com consistência.</p>
                            <a
                                href={wa(MSG_TRIMESTRAL)}
                                className={styles.planWhatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <WhatsappIcon height="18" color="#ffffff" />
                                <span>Quero o plano trimestral</span>
                            </a>
                        </div>

                        <div className={styles.planCard}>
                            <p className={styles.planName}>Plano semestral</p>
                            <p className={styles.planPeriod}>A partir de R$ 579,00 ou R$ 600,00 em até 6x</p>
                            <p className={styles.planPrice}>Para quem está em total comprometimento com a evolução.</p>
                            <a
                                href={wa(MSG_SEMESTRAL)}
                                className={styles.planWhatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <WhatsappIcon height="18" color="#ffffff" />
                                <span>Quero o plano semestral</span>
                            </a>
                        </div>
                    </section>
                </div>

                <p className={styles.footerNote}>
                    Valores divulgados para referência; condições de pagamento e vigência podem ser confirmadas no
                    atendimento. Resultados dependem de adesão ao programa e da sua consistência.
                </p>

                <div className={styles.ctaSection} aria-labelledby="cta-final-heading">
                    <h2 id="cta-final-heading" className={styles.ctaHeading}>
                        Ainda em dúvida sobre qual plano escolher?
                    </h2>
                    <p className={styles.ctaText}>
                        Me chame no WhatsApp. Em poucos minutos alinhamos seu objetivo, sua rotina e o investimento que
                        faz mais sentido para você começar com segurança.
                    </p>
                    <a
                        href={wa(MSG_GERAL)}
                        className={styles.ctaWhatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <WhatsappIcon height="22" color="#ffffff" />
                        <span>Falar com o Anderson</span>
                    </a>
                </div>
            </div>
        </div>
    );
};

export default Andersoncruz_personal_consultoriapersonalizada;
